import React, {
  useEffect, useRef, useState, useId,
} from 'react';
import PropTypes from 'prop-types';
import DatePicker, { CalendarContainer } from 'react-datepicker';
import moment from 'moment';
import './dobPicker.scss';
import { IMask, IMaskInput } from 'react-imask';
import { ReactComponent as ClearSvg } from '../../../../../assets/icons/close.svg';
import { ReactComponent as CalendarSvg } from '../../../../../assets/icons/calendar.svg';

const calendarData = {
  days: ['s', 'm', 't', 'w', 't', 'f', 's'],
  months: ['January', 'February', 'March', 'April',
    'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December'],
};

const locale = {
  match: {},
  localize: {
    day: (n) => calendarData.days[n],
    month: (n) => calendarData.months[n],
  },
  formatLong: {
    date: () => 'mm-dd-yyyy',
  },
};

const DOBPicker = ({
  date = '', onChange, label = '', height = 50, labelDefaultPosition = 12, labelBG = '#FFFFFF', error = '',
  errorClassName = '', placeholder = '', closeOnScroll = false, hideClearButton = false, dateFormat = 'MM/DD/YYYY',
  wrapperClassName = '', minDate = null, autoValidation = true, inputFormat = 'MM.DD.YYYY', disabled,
}) => {
  const uniqId = useId();
  const fieldRef = useRef();
  const [inputValue, setInputValue] = useState(date && moment(date).isValid() ? moment(date).format(inputFormat) : '');
  const [isValid, validToggle] = useState(true);
  const [isCalendarOpen, calendarToggle] = useState(false);

  const changeDate = (currentDate) => {
    if (isCalendarOpen) {
      onChange(moment(currentDate).format(dateFormat));

      setInputValue(moment(currentDate).format(inputFormat));

      calendarToggle(false);
    }
  };

  useEffect(() => {
    if (date?.length === 10) {
      setInputValue(moment(date).format(inputFormat));
    } else if (date === ' ') {
      setInputValue('');
    }
  }, [date]);

  const changeValue = async (value) => {
    const [formatSymbol] = dateFormat.match(/\W/);

    setInputValue(value);

    validToggle(true);

    if (value !== inputValue) {
      if (value?.length === 10) {
        if (isCalendarOpen) {
          calendarToggle(false);

          await onChange(value.replace(/\./g, formatSymbol));

          calendarToggle(true);
        } else {
          onChange(value.replace(/\./g, formatSymbol));
        }
      } else if (!autoValidation) {
        onChange(value.replace(/\./g, formatSymbol));
      } else {
        onChange('');
      }
    }
  };

  const onBlur = () => {
    if (inputValue.length && inputValue.length !== 10 && autoValidation) {
      validToggle(false);
    }
  };

  const clearDate = () => {
    setInputValue('');

    onChange('');
  };

  const closeOnScrolling = () => {
    if (closeOnScroll) {
      calendarToggle(false);
    }
  };

  const calendarContainer = ({ className, children }) => (
    <div className="date_piker_wrapper" style={{ width: fieldRef?.current?.offsetWidth, minWidth: 210 }}>
      <CalendarContainer className={className}>
        <div style={{ position: 'relative' }}>{children}</div>
      </CalendarContainer>
    </div>
  );

  return (
    <div
      ref={fieldRef}
      className={`calendar_wrapper single ${disabled ? 'disabled' : ''} ${wrapperClassName}`}
    >
      <DatePicker
        customInput={(
          <div
            style={{ height }}
            className="dob_input_wrapper"
          >
            <div
              id={`dob-container-${uniqId}`}
              className={`dob_wrapper ${isCalendarOpen ? 'active' : ''} ${(placeholder || inputValue)
                ? 'with_value' : ''} ${(error || !isValid) ? 'error' : ''}`}
            >
              {/* <div */}
              {/*  className="dob_title" */}
              {/* > */}
              {/*  {date && moment(date).format('MM.DD.YYYY')} */}

              {/*  {placeholder && !date */}
              {/*      && ( */}
              {/*      <span className="placeholder"> */}
              {/*        {moment(placeholder).isValid() */}
              {/*          ? moment(placeholder).format('MM.DD.YYYY') */}
              {/*          : placeholder} */}
              {/*      </span> */}
              {/*      )} */}
              {/* </div> */}

              <IMaskInput
                className="dob_title"
                mask="mm.dd.yyyy"
                value={inputValue}
                blocks={{
                  yyyy: {
                    mask: '0000',
                  },
                  mm: {
                    mask: IMask.MaskedRange,
                    from: 1,
                    to: 12,
                  },
                  dd: {
                    mask: IMask.MaskedRange,
                    from: 1,
                    to: 31,
                  },
                }}
                unmask={false}
                placeholder={placeholder || 'mm.dd.yyyy'}
                onChange={() => {}}
                onAccept={(val, mask) => changeValue(mask.value)}
                onBlur={onBlur}
                disabled={disabled}
              />

              <CalendarSvg
                className="calendar_svg"
                onClick={() => !disabled && calendarToggle((prev) => !prev)}
              />

              {label
                  && (
                    <label
                      style={{
                        '--default-position': `${labelDefaultPosition}px`,
                        '--label-bg': `${labelBG}`,
                      }}
                    >
                      {label}
                    </label>
                  )}

              {!!error && typeof error === 'string' && <p className={`error_text ${errorClassName}`}>{error}</p>}
            </div>

            {inputValue && !hideClearButton && (
            <ClearSvg
              className="clear_svg"
              onClick={() => clearDate('')}
            />
            )}
          </div>
          )}
        selected={date?.length === 10 ? new Date(moment(date).format('MM/DD/YYYY')) : null}
        onChange={changeDate}
        onClickOutside={() => calendarToggle(false)}
        yearDropdownItemNumber={100}
        showYearDropdown
        minDate={minDate}
        portalId={`dob-portal-${uniqId}`}
        locale={locale}
        closeOnScroll={closeOnScrolling}
        scrollableYearDropdown
        open={isCalendarOpen}
        calendarContainer={calendarContainer}
      />
    </div>
  );
};

DOBPicker.propTypes = {
  date: PropTypes.string,
  label: PropTypes.string,
  height: PropTypes.number,
  labelDefaultPosition: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.any,
  labelBG: PropTypes.string,
  errorClassName: PropTypes.string,
  dateFormat: PropTypes.string,
  placeholder: PropTypes.string,
  wrapperClassName: PropTypes.string,
  closeOnScroll: PropTypes.bool,
  hideClearButton: PropTypes.bool,
  autoValidation: PropTypes.bool,
  minDate: PropTypes.any,
  inputFormat: PropTypes.string,
};

export default DOBPicker;
